import { Box, Stack } from '@chakra-ui/react';
import InputComponent from '@components/InputComponent/InputComponent';
import SelectComponent from '@components/SelectComponent/SelectComponent';
import { toCamelCase } from '@utils/functions';
import { FC, useCallback, useEffect } from 'react';
import {
    Control,
    FieldError,
    FieldErrorsImpl,
    FieldValues,
    Merge,
    UseFormRegister,
    UseFormTrigger,
    UseFormWatch,
} from 'react-hook-form';
import { ComboInputObject, SelectItem } from 'types/formElement';

interface Props {
    labelSelect: string;
    labelInput: string;
    options: SelectItem[];
    value: ComboInputObject;
    registerInput: UseFormRegister<FieldValues>;
    controlSelect: Control<FieldValues>;
    onChange: (values: ComboInputObject) => void;
    isRequired?: boolean;
    isInvalidInput?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | null;
    maxInputLength?: number;
    minInputLength?: number;
    inputType?: string;
    validateInput: (value: ComboInputObject) => string | boolean;
    trigger: UseFormTrigger<FieldValues>;
    watch: UseFormWatch<FieldValues>;
}

const SelectInputComponent: FC<Props> = ({
    labelSelect,
    labelInput,
    options,
    value,
    registerInput,
    controlSelect,
    onChange,
    isRequired = false,
    isInvalidInput = null,
    minInputLength = 0,
    maxInputLength = 0,
    inputType = 'text',
    validateInput,
    trigger,
    watch,
}) => {
    useEffect(() => {
        if (value.selectValue) {
            trigger(toCamelCase(labelInput));
        }
    }, [labelInput, watch(toCamelCase(labelSelect))]);

    const handleValidate = useCallback(
        (valueInput: string) => {
            return validateInput({ inputValue: valueInput, selectValue: value.selectValue, isRequired });
        },
        [isRequired, validateInput, value.selectValue],
    );

    const handleChangeSelect = useCallback(
        (selectValue: string | number) => {
            onChange({ ...value, selectValue: selectValue });
            handleValidate(value.inputValue);
        },
        [handleValidate, onChange, value],
    );

    const handleChangeInput = useCallback(
        (inputValue: string) => {
            onChange({ ...value, inputValue: inputValue });
        },
        [onChange, value],
    );

    return (
        <Stack direction={'row'} spacing="0" flex={1} mb={5} border="1px solid #E2E8F0" borderRadius={4}>
            <SelectComponent
                id={toCamelCase(labelSelect)}
                label={labelSelect}
                options={options}
                valueComponent={value.selectValue}
                onChangeComponent={handleChangeSelect}
                flex={1.65}
                isCompact
                isRequired={isRequired}
                control={controlSelect}
                watch={watch}
            />
            <Box
                display={{ base: 'none', lg: 'flex' }}
                alignItems="center"
                justifyContent="center"
                bg="white"
                width="1px"
                h="100%"
                border="1px solid"
                borderColor="white"
                borderWidth="3px 0 3px 0"
            >
                <Box width="1px" h="34px" bg="#E2E8F0"></Box>
            </Box>
            <InputComponent
                id={toCamelCase(labelInput)}
                register={registerInput}
                value={value.inputValue}
                label={labelInput}
                flex={3}
                isCompact
                onChange={handleChangeInput}
                isRequired={isRequired}
                isInvalid={isInvalidInput}
                maxLength={maxInputLength}
                minLength={minInputLength}
                type={inputType}
                validate={handleValidate}
            />
        </Stack>
    );
};

export default SelectInputComponent;
